/*
  Gemeente-zoekveld. Matchen gebeurt op een genormaliseerde naam: kleine letters, zonder accenten,
  apostrof, koppelteken of dubbele spaties. Zo vindt "s hertogenbosch" ook 's-Hertogenbosch.
  Gangbare volksnamen staan in ALIASES; ze tellen alleen als de officiële naam in de lijst staat.
*/
// In een functie gewikkeld: de snippet kan twee keer op een pagina staan, en dan mag const niet botsen.
(() => {
const ALIASES = {
  'den bosch': 's hertogenbosch',
  'den haag': 's gravenhage',
};

function normalize(value) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['\u2018\u2019`-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

class GroundGemeenteSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[role="combobox"]');
    this.listbox = this.querySelector('[role="listbox"]');
    this.empty = this.querySelector('.ground-combobox__empty');
    this.emptyQuery = this.querySelector('[data-empty-query]');
    this.emptyContact = this.querySelector('[data-empty-contact]');
    const dataScript = this.querySelector('.ground-combobox__data');

    this.items = [];
    if (dataScript) {
      try {
        this.items = JSON.parse(dataScript.textContent).map((item) => ({ ...item, key: normalize(item.name) }));
      } catch (e) {
        this.items = [];
      }
    }
    this.activeIndex = -1;

    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.input.addEventListener('blur', () => {
      window.setTimeout(() => this.close(), 150);
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });
  }

  search(raw) {
    const query = normalize(raw);
    const alias = ALIASES[query];
    return this.items.filter((item) => item.key.indexOf(query) !== -1 || (alias && item.key.indexOf(alias) !== -1));
  }

  onInput() {
    const raw = this.input.value.trim();
    if (raw === '') {
      this.close();
      this.hideEmpty();
      return;
    }
    const matches = this.search(raw);
    // Pas na twee tekens een lege staat tonen: bij één letter is "niet gevonden" voorbarig.
    if (matches.length === 0 && normalize(raw).length >= 2) {
      this.close();
      this.showEmpty(raw);
      return;
    }
    this.hideEmpty();
    this.render(matches);
  }

  render(matches) {
    this.listbox.innerHTML = '';
    this.activeIndex = -1;
    if (matches.length === 0) {
      this.close();
      return;
    }

    matches.forEach((item, index) => {
      const option = document.createElement('li');
      option.setAttribute('role', 'option');
      option.setAttribute('id', this.input.id + '-option-' + index);
      option.className = 'ground-combobox__option';
      option.textContent = item.name;
      option.addEventListener('mousedown', (event) => {
        event.preventDefault();
        this.select(item);
      });
      this.listbox.appendChild(option);
    });
    this.open();
  }

  showEmpty(raw) {
    if (!this.empty) return;
    this.emptyQuery.textContent = raw;
    this.emptyContact.href = '/pages/contact?gemeente=' + encodeURIComponent(raw);
    this.empty.hidden = false;
  }

  hideEmpty() {
    if (this.empty) this.empty.hidden = true;
  }

  onKeydown(event) {
    const options = Array.prototype.slice.call(this.listbox.querySelectorAll('[role="option"]'));

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!options.length) return;
      this.activeIndex = (this.activeIndex + 1) % options.length;
      this.highlight(options);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!options.length) return;
      this.activeIndex = (this.activeIndex - 1 + options.length) % options.length;
      this.highlight(options);
    } else if (event.key === 'Enter') {
      // Enter zonder gemarkeerde optie kiest de enige of eerste treffer.
      const index = this.activeIndex > -1 ? this.activeIndex : 0;
      if (options[index]) {
        event.preventDefault();
        const name = options[index].textContent;
        const match = this.items.filter((item) => item.name === name)[0];
        if (match) this.select(match);
      }
    } else if (event.key === 'Escape') {
      this.close();
    }
  }

  highlight(options) {
    options.forEach((option, index) => {
      const active = index === this.activeIndex;
      option.classList.toggle('is-active', active);
      option.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    const active = options[this.activeIndex];
    if (active) this.input.setAttribute('aria-activedescendant', active.id);
  }

  select(item) {
    this.input.value = item.name;
    this.close();
    window.location.href = item.url;
  }

  open() {
    this.listbox.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.listbox.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }
}

if (!customElements.get('ground-gemeente-search')) {
  customElements.define('ground-gemeente-search', GroundGemeenteSearch);
}
})();
